import { categoryDtoMap } from "./categoryDtoMap";
import { categoryRouteMap } from "./categoryRouteMap";
import { emissionActivityModalAPI } from "../API/emissionActivityAPI";
import { emissionFactorAPI } from "../API/emissionFactorAPI";
import { flattenEAFormData } from "../modals/form/utils/toRequestBody";
import { dataEntryAPI } from "../API/dataEntryAPI";
import { emissionAPI } from "../API/emissionAPI";
import dayjs from "dayjs";
import { format } from "date-fns";
import { importJobAPI } from "../../ExcelImportExportModal/API/importJobAPI";
import {
  DATA_ENTRY_CALCULATION_IDS,
  calculateWeightDistance,
} from "../utils/dataEntryCalculation";

const API = emissionActivityModalAPI;
const DEAPI = dataEntryAPI;
const EFAPI = emissionFactorAPI;
const EMAPI = emissionAPI;

// Month/Day 資料依實際日曆年存檔（可早於報告年度），Year/Interval 沿用報告年度
const resolveEntryYear = (dataFormat, usedDate, reportingYear) =>
  (dataFormat == 1 || dataFormat == 3) && dayjs(usedDate).isValid()
    ? dayjs(usedDate).year()
    : reportingYear;

export const emissionActivityService = {
  //////// 取得排放活動列表
  fetchEAListData(cId, year, keyword, selectedTab, selectedScope, standardId) {
    const routeCat = categoryRouteMap[selectedTab];
    const dtoCat = categoryDtoMap[selectedTab];

    const requestBody = {
      companyIds: cId,
      year: year,
      category: dtoCat,
      keyword: keyword,
      standardId: standardId,
    };

    const apiFn =
      selectedScope === "CO₂ Emission From Biomass/Bioenergy"
        ? API.getBiomassEAList
        : API.getEAList;

    const res = apiFn(requestBody, routeCat);
    return res;
  },

  fetchRemovalMitigationListData(companyIds, year, gwpId) {
    const requestBody = {
      companyIds,
      year: Number(year),
      gwpId: Number(gwpId),
    };

    return API.getRemovalMitigationList(requestBody);
  },

  //////// 取得單筆排放活動
  deleteRemovalMitigation(id) {
    return API.deleteRemovalMitigation(id);
  },

  fetchSingleEAData(selectedTab, id, categoryISO64) {
    const routeCat = categoryRouteMap[selectedTab];
    return API.editEmissionActivityAPI(routeCat, id, categoryISO64);
  },

  //////// 新增排放活動
  addEA(
    year,
    EAFormData,
    EFFormData,
    EFLandFormData,
    landEAFormData,
    schema,
    selectedTab,
    standardId,
  ) {
    const cat = categoryRouteMap[selectedTab].replace(/^cat/, "");
    const ghgCat = categoryDtoMap[selectedTab];
    const specialCats = ["4_3"];
    const addAPI = API[schema.addAPI];

    let params;
    if (!specialCats.includes(cat)) {
      params = [
        year,
        flattenEAFormData(EAFormData),
        flattenEAFormData(EFFormData),
        ghgCat,
        standardId,
      ];
    } else if (cat === "4_3") {
      params = [
        year,
        flattenEAFormData(EAFormData),
        flattenEAFormData(landEAFormData),
        flattenEAFormData(EFFormData),
        flattenEAFormData(EFLandFormData),
        standardId,
      ];
    }

    return addAPI(...params);
  },

  //////// 修改排放活動
  editEA(
    EAFormData,
    landEAFormData,
    EFFormData,
    EFLandFormData,
    EFSeaFormData,
    EFAirFormData,
    selectedTab,
  ) {
    const routeCat = categoryRouteMap[selectedTab];
    const cat = routeCat.replace(/^cat/, "");
    const specialCats = ["4_3"];

    let params;

    if (!specialCats.includes(cat)) {
      params = [
        routeCat,
        flattenEAFormData(EAFormData),
        null,
        flattenEAFormData(EFFormData),
      ];
    } else {
      params = [
        routeCat,
        flattenEAFormData(EAFormData),
        flattenEAFormData(landEAFormData),
        flattenEAFormData(EFFormData),
        cat,
        flattenEAFormData(EFLandFormData),
        flattenEAFormData(EFSeaFormData),
        flattenEAFormData(EFAirFormData),
      ];
    }

    return API.modifyEmissionActivityAPI(...params);
  },

  //////// 刪除排放活動
  deleteEA(selectedTab, id) {
    const routeCat = categoryRouteMap[selectedTab];
    return API.deleteEmissionActivityAPI(routeCat, id);
  },

  //////// 下載排放活動Excel
  downloadEAExcel(authComArr, lang, year, selectedTab, standardId) {
    const routeCat = categoryRouteMap[selectedTab];
    const dtoCat = categoryDtoMap[selectedTab];
    const category = routeCat.replace(/^cat/, "");

    const requestBody = {
      companyIds: authComArr,
      year: year,
      category: category,
      ghgCategory: dtoCat,
      lang: lang,
      standardId: standardId,
    };

    return API.downloadEmissionActivityExcel(requestBody, routeCat);
  },

  //////// 上傳排放活動Excel
  uploadEAExcel(authComArr, year, lang, selectedTab, standardId, file) {
    const formData = new FormData();
    const routeCat = categoryRouteMap[selectedTab];
    const dtoCat = categoryDtoMap[selectedTab];
    const category = routeCat.replace(/^cat/, "");

    const requestBody = {
      companyIds: authComArr,
      year: year,
      category: category,
      ghgCategory: dtoCat,
      lang: lang,
      standardId: standardId,
    };

    formData.append("file", file);
    formData.append("dto", JSON.stringify(requestBody));

    return API.uploadEmissionActivityEFExcel(formData, routeCat);
  },

  //////// 上傳排放活動Excel
  bulkUploadEAExcel(
    type,
    authComArr,
    year,
    lang,
    selectedTab,
    standardId,
    file,
  ) {
    const formData = new FormData();
    const routeCat = categoryRouteMap[selectedTab];
    const dtoCat = categoryDtoMap[selectedTab];
    const category = routeCat.replace(/^cat/, "");

    const requestBody = {
      importType: type,
      companyIds: authComArr,
      year: year,
      category: category,
      ghgCategory: dtoCat,
      lang: lang,
      standardId: standardId,
    };

    formData.append("file", file);
    formData.append("dto", JSON.stringify(requestBody));

    return importJobAPI.submitImportJob(formData);
  },

  //////// 取得數據輸入列表
  fetchDEListData(cId, year, keyword, selectedTab, selectedScope, standardId) {
    const routeCat = categoryRouteMap[selectedTab].split("_")[0]; //cat1
    const dtoCat = categoryDtoMap[selectedTab];

    const requestBody = {
      companyIds: cId,
      year: year,
      category: dtoCat,
      keyword: keyword,
      standardId: standardId,
    };

    const apiFn =
      selectedScope === "CO₂ Emission From Biomass/Bioenergy"
        ? DEAPI.getBiomassDEList
        : DEAPI.getDEList;

    const res = apiFn(requestBody, routeCat);
    return res;
  },

  //////// 設定數據輸入
  setDE(activityDataId, selectedTab, format, unit) {
    const routeCat = categoryRouteMap[selectedTab].split("_")[0];
    const requestBody = {
      dataUnitId: unit,
      ...(format && { dataTypeId: format }), // 只有當 format 有值時才加入 dataTypeId
    };

    const res = DEAPI.setDE(activityDataId, requestBody, routeCat);
    return res;
  },

  //////// 新增數據輸入
  addDE(
    dataFormat,
    usedDate,
    confirmedRange,
    consumptionValue,
    catFields,
    isCat3x,
    isCat3basic,
    calculationId,
    selectedTab,
    companyId,
    activityDataId,
    year,
  ) {
    const routeCat = categoryRouteMap[selectedTab].split("_")[0];

    const formattedDate = dayjs(usedDate).isValid()
      ? dayjs(usedDate).format("YYYY-MM-DD")
      : null;
    //date range
    const startDate =
      confirmedRange && confirmedRange.startDate
        ? format(confirmedRange.startDate, "yyyy-MM-dd")
        : null;

    const endDate =
      confirmedRange && confirmedRange.endDate
        ? format(confirmedRange.endDate, "yyyy-MM-dd")
        : null;

    const convertedToMonth =
      dataFormat == 1 || dataFormat == 3
        ? String(parseInt(formattedDate.split("-")[1], 10))
        : "0";
    const convertedToDay =
      dataFormat == 3 ? String(parseInt(formattedDate.split("-")[2], 10)) : "0";

    const numericConsumption = Number(consumptionValue) || 0;
    let requestBody = {
      companyId: companyId || "",
      customerActivityDataId: activityDataId || "",
      dataTypeId: dataFormat,
      year: resolveEntryYear(dataFormat, usedDate, year) || 0,
      month: convertedToMonth,
      day: convertedToDay, // 永遠包含此欄位
      rawData: numericConsumption,
    };
    // 只有在 startDate 和 endDate 有值時才加入
    if (startDate) {
      requestBody.startDate = startDate;
    }
    if (endDate) {
      requestBody.endDate = endDate;
    }

    // cat 3_3 / 3_4 / 3_5
    if (isCat3x) {
      // origin / destination 非必填，有值才帶入
      if (catFields.origin) requestBody.origin = catFields.origin;
      if (catFields.destination)
        requestBody.destination = catFields.destination;
      requestBody.distance = Number(catFields.distance_3_3) || 0;
      requestBody.passengerCount = Number(catFields.passengers) || 0;
      requestBody.tripType = Number(catFields.tripTypeValue) || 1;
      requestBody.trips = Number(catFields.numberOfTrips) || 0;

      if (calculationId === DATA_ENTRY_CALCULATION_IDS.FUEL) {
        requestBody.weight = numericConsumption;
        requestBody.distance = 0;
      }
    }

    // cat 3_1 / 3_2：固定帶入 weight / distance，依計算模式決定 rawData
    if (isCat3basic) {
      if (catFields.origin) requestBody.origin = catFields.origin;
      if (catFields.destination) requestBody.destination = catFields.destination;

      if (calculationId === DATA_ENTRY_CALCULATION_IDS.WEIGHT_DISTANCE) {
        requestBody.weight = Number(catFields.weight) || 0;
        requestBody.distance = Number(catFields.distance) || 0;
        requestBody.rawData =
          calculateWeightDistance(
            requestBody.weight,
            requestBody.distance,
          ) ?? 0;
      } else if (calculationId === DATA_ENTRY_CALCULATION_IDS.DISTANCE) {
        requestBody.weight = 0;
        requestBody.distance = Number(catFields.distance) || 0;
        requestBody.rawData = requestBody.distance;
      } else if (calculationId === DATA_ENTRY_CALCULATION_IDS.FUEL) {
        requestBody.weight = numericConsumption;
        requestBody.distance = 0;
        requestBody.rawData = numericConsumption;
      } else {
        requestBody.weight = 0;
        requestBody.distance = 0;
      }
    }

    const res = DEAPI.addDE(requestBody, routeCat);
    return res;
  },

  //////// 修改數據輸入
  editDE(
    dataFormat,
    usedDate,
    confirmedRange,
    consumptionValue,
    catFields,
    isCat3x,
    isCat3basic,
    calculationId,
    selectedTab,
    companyId,
    year,
    dataEntryItemId,
  ) {
    const routeCat = categoryRouteMap[selectedTab].split("_")[0];

    const formattedDate = dayjs(usedDate).isValid()
      ? dayjs(usedDate).format("YYYY-MM-DD")
      : null;
    //date range
    const startDate =
      confirmedRange && confirmedRange.startDate
        ? format(confirmedRange.startDate, "yyyy-MM-dd")
        : null;

    const endDate =
      confirmedRange && confirmedRange.endDate
        ? format(confirmedRange.endDate, "yyyy-MM-dd")
        : null;

    const convertedToMonth =
      dataFormat == 1 || dataFormat == 3
        ? String(parseInt(formattedDate.split("-")[1], 10))
        : "0";
    const convertedToDay =
      dataFormat == 3 ? String(parseInt(formattedDate.split("-")[2], 10)) : "0";

    const numericConsumption = Number(consumptionValue) || 0;
    let requestBody = {
      companyId: companyId || "",
      year: resolveEntryYear(dataFormat, usedDate, year) || 0,
      month: convertedToMonth,
      day: convertedToDay, // 永遠包含此欄位
      rawData: numericConsumption,
    };
    // 只有在 startDate 和 endDate 有值時才加入
    if (startDate) {
      requestBody.startDate = startDate;
    }
    if (endDate) {
      requestBody.endDate = endDate;
    }

    if (isCat3x) {
      requestBody.origin = catFields.origin || "";
      requestBody.destination = catFields.destination || "";
      requestBody.distance = Number(catFields.distance_3_3) || 0;
      if (catFields.passengers) {
        requestBody.passengerCount = Number(catFields.passengers) || 0;
      }
      requestBody.tripType = Number(catFields.tripTypeValue) || 1;
      requestBody.trips = Number(catFields.numberOfTrips) || 0;

      if (calculationId === DATA_ENTRY_CALCULATION_IDS.FUEL) {
        requestBody.weight = numericConsumption;
        requestBody.distance = 0;
      }
    }

    if (isCat3basic) {
      if (catFields.origin) requestBody.origin = catFields.origin;
      if (catFields.destination) requestBody.destination = catFields.destination;

      if (calculationId === DATA_ENTRY_CALCULATION_IDS.WEIGHT_DISTANCE) {
        requestBody.weight = Number(catFields.weight) || 0;
        requestBody.distance = Number(catFields.distance) || 0;
        requestBody.rawData =
          calculateWeightDistance(
            requestBody.weight,
            requestBody.distance,
          ) ?? 0;
      } else if (calculationId === DATA_ENTRY_CALCULATION_IDS.DISTANCE) {
        requestBody.weight = 0;
        requestBody.distance = Number(catFields.distance) || 0;
        requestBody.rawData = requestBody.distance;
      } else if (calculationId === DATA_ENTRY_CALCULATION_IDS.FUEL) {
        requestBody.weight = numericConsumption;
        requestBody.distance = 0;
        requestBody.rawData = numericConsumption;
      } else {
        requestBody.weight = 0;
        requestBody.distance = 0;
      }
    }

    const res = DEAPI.editDE(dataEntryItemId, requestBody, routeCat);
    return res;
  },

  //////// 下載數據輸入Excel
  downloadDEExcel(
    authComArr,
    lang,
    year,
    selectedTab,
    rootId,
    standardId,
    isAll = false,
    isSummary = false,
  ) {
    const routeCat = categoryRouteMap[selectedTab] || "";
    const dtoCat = categoryDtoMap[selectedTab] || "";
    const category = isAll ? "all" : routeCat.replace(/^cat/, "");

    const requestBody = {
      companyIds: authComArr,
      year: year,
      category: category,
      ghgCategory: dtoCat,
      lang: lang,
      rootId: rootId,
      standardId: standardId,
    };

    return DEAPI.downloadDataEntryExcel({ ...requestBody, isSummary });
  },

  //////// 上傳數據輸入Excel
  uploadDEExcel(authComArr, year, file, selectedTab, lang, standardId) {
    const formData = new FormData();
    const routeCat = categoryRouteMap[selectedTab];
    const dtoCat = categoryDtoMap[selectedTab];
    const category = routeCat.replace(/^cat/, "");

    const requestBody = {
      companyIds: authComArr,
      year: year,
      category: category,
      lang: lang,
      standardId: standardId,
      ghgCategory: dtoCat,
    };

    formData.append("file", file);
    formData.append("dto", JSON.stringify(requestBody));

    return DEAPI.uploadDataEntryExcel(formData);
  },

  ///////大量上傳數據輸入Excel
  bulkUploadDEExcel(
    type,
    authComArr,
    year,
    file,
    selectedTab,
    lang,
    standardId,
  ) {
    const routeCat = categoryRouteMap[selectedTab];
    const dtoCat = categoryDtoMap[selectedTab];
    const category = routeCat.replace(/^cat/, "");

    const SPECIAL_IMPORT_TYPE_MAP = {
      "3_3": "cat3_3_dataEntry",
      "3_4": "cat3_4_dataEntry",
      "3_5": "cat3_5_dataEntry",
    };

    const importType =
      type === "dataEntry" ? (SPECIAL_IMPORT_TYPE_MAP[category] ?? type) : type;
    const dto = JSON.stringify({
      importType,
      companyIds: authComArr,
      year,
      category,
      lang,
      standardId,
      ghgCategory: dtoCat,
    });

    const formData = new FormData();
    formData.append("file", file);
    formData.append("dto", dto);

    return importJobAPI.submitImportJob(formData);
  },

  //////刪除數據輸入
  deleteDE(selectedTab, companyId, dataEntryItemId) {
    const routeCat = categoryRouteMap[selectedTab].split("_")[0];
    return DEAPI.deleteDE(routeCat, companyId, dataEntryItemId);
  },

  //////// 取得排放係數列表
  fetchEFListData(cId, year, keyword, isBiomass, selectedTab, standardId) {
    const cat = categoryDtoMap[selectedTab];
    const routeCat = categoryRouteMap[selectedTab]; //////// cat1_1 cat1_2...
    const catName = routeCat.split("_")[0]; //////// cat1 cat2 cat3...

    const requestBody = {
      companyIds: cId,
      year: year,
      category: cat,
      keyword: keyword,
      standardId: standardId,
    };

    const apiFn = isBiomass ? EFAPI.getBiomassEFList : EFAPI.getEFList;

    const res = apiFn(requestBody, catName);
    return res;
  },

  //////// 取得單筆排放係數
  async fetchSingleEFData(selectedTab, id) {
    const routeCat = categoryRouteMap[selectedTab];
    const catName = routeCat.split("_")[0];
    const paramCat = routeCat.replace("cat", "");

    const data = await EFAPI.editEmissionFactorAPI(catName, paramCat, id);

    const shouldUseCustomEF =
      data?.useCustomEFLevel === true || Number(data?.useCustomEFLevel) > 0;

    return shouldUseCustomEF ? { ...data, emissionFactorTypeId: 1 } : data;
  },

  //////// 修改排放係數
  editEF(EFFormData, EAFormData, EFLandFormData, selectedTab) {
    const specialCats = ["4_3"];
    const cat = categoryDtoMap[selectedTab]; //////// 1_1 1_2...
    const routeCat = categoryRouteMap[selectedTab]; //////// cat1_1 cat1_2...
    const catName = routeCat.split("_")[0]; //////// cat1 cat2 cat3...

    if (!specialCats.includes(cat)) {
      if (
        cat === "5_1" ||
        cat === "5_2" ||
        cat === "5_3" ||
        flattenEAFormData(EFFormData).otherEFNote
      ) {
        return (async () => {
          await API.modifyEmissionActivityAPI(
            routeCat,
            flattenEAFormData(EAFormData),
            null,
            flattenEAFormData(EFFormData),
          );
          return EFAPI.modifyEF(catName, flattenEAFormData(EFFormData));
        })();
      }
      return EFAPI.modifyEF(catName, flattenEAFormData(EFFormData));
    }

    if (cat === "4_3") {
      const { emissionFactorTypeId, emissionFactorId, id } =
        flattenEAFormData(EFFormData);
      const { landEFTypeId, landEFId } = flattenEAFormData(EFLandFormData);

      return EFAPI.cat4_3modifyEF(
        id,
        emissionFactorTypeId,
        emissionFactorId,
        landEFTypeId,
        landEFId,
      );
    }
  },

  //////// 取得排放係數level下拉選單
  fetchEFLevelDropdown(selectedTab, catName, combineString, EAFormData) {
    const ghgCategory = categoryDtoMap[selectedTab];

    const requestBody = {
      level: combineString,
      scope2AcitivityId: catName === "cat2" ? EAFormData?.activityId : 0,
      cat3CalculationId: catName === "cat3" ? EAFormData?.calculationId : 0,
      ghgCat3_3ActivityId:
        catName === "cat4" ? EAFormData?.ghgActivityNameId : 0,
      isBiomassActivity: EAFormData.isBiomassActivity || false,
      isBiomassSeparate: EAFormData.isBiomassSeparate || false,
      ghgCategory: ghgCategory || null,
    };

    const data = EFAPI.emissionLevel(requestBody);

    return data;
  },

  //////// 下載排放係數Excel
  downloadEFExcel(authComArr, year, selectedTab, standardId, lang, isAll) {
    const routeCat = categoryRouteMap[selectedTab];
    const dtoCat = categoryDtoMap[selectedTab];
    const category = routeCat.replace(/^cat/, "");

    const requestBody = {
      companyIds: authComArr,
      year: year,
      category: isAll ? "" : category,
      ghgCategory: dtoCat,
      lang: lang,
      standardId: standardId,
    };

    return EFAPI.downloadEmissionFactorExcel(requestBody);
  },

  //////// 上傳排放係數Excel
  uploadEFExcel(file, selectedTab, standardId) {
    const dtoCat = categoryDtoMap[selectedTab];

    const requestBody = {
      ghgCategory: dtoCat,
      standardId: standardId,
    };

    const formData = new FormData();
    formData.append("file", file);
    formData.append("dto", JSON.stringify(requestBody));

    return EFAPI.uploadEmissionFactorExcel(formData);
  },

  //////// 大量上傳排放係數Excel
  bulkUploadEFExcel(type, file, selectedTab, standardId) {
    const dtoCat = categoryDtoMap[selectedTab];

    const requestBody = {
      importType: type,
      ghgCategory: dtoCat,
      standardId: standardId,
    };

    const formData = new FormData();
    formData.append("file", file);
    formData.append("dto", JSON.stringify(requestBody));

    return importJobAPI.submitImportJob(formData);
  },

  ///////取得排放當量列表
  fetchEMListData(
    cId,
    year,
    keyword,
    selectedTab,
    selectedScope,
    standardId,
    unitId,
    gwpId,
  ) {
    const routeCat = categoryRouteMap[selectedTab].split("_")[0]; //cat1
    const dtoCat = categoryDtoMap[selectedTab];

    const requestBody = {
      companyIds: cId,
      year: year,
      category: dtoCat,
      keyword: keyword,
      standardId: standardId,
      unitId: unitId,
      gwpId: gwpId,
    };

    const apiFn =
      selectedScope === "CO₂ Emission From Biomass/Bioenergy"
        ? EMAPI.getBiomassEMList
        : EMAPI.getEMList;

    const res = apiFn(requestBody, routeCat);
    return res;
  },

  //////// 設定單位
  setUnit(scope, id, unit) {
    return DEAPI.setUnit(scope, id, unit);
  },

  //////// 觸發重新計算排放當量
  recalculate(rootId, year) {
    return EMAPI.recalculate(rootId, year);
  },

  //////// 查詢重新計算任務狀態
  getRecalculateStatus(taskId) {
    return EMAPI.getRecalculateStatus(taskId);
  },
};
